// src/components/Remove2.jsx
import React, { useEffect, useState, useRef } from "react";
import Popup from "reactjs-popup";
import SignatureCanvas from "react-signature-canvas";
import { useNavigate } from "react-router-dom";
import "reactjs-popup/dist/index.css";
import { FaTimesCircle } from "react-icons/fa";
import { registerLocale } from "react-datepicker";
import { et } from "date-fns/locale";
import { fetchMeterBrands, fetchDiameters, fetchQnoms, searchMeters } from "../../data/fetchData";
import { useNotification } from "../../context/NotificationContext";
import { formatDecimal } from "../../utils/formatters";

registerLocale("et", et);

const TYPE_OPTIONS = [
  { value: "MAIN_GAUGE", label: "Põhiarvesti" },
  { value: "SUB_GAUGE", label: "Alamarvesti" },
  { value: "WATERING", label: "Kastmine" },
];

export default function Remove2() {
  const [data, setData] = useState({});
  const [brandOptions, setBrandOptions] = useState([]);
  const [diameterOptions, setDiameterOptions] = useState([]);
  const [qnOptions, setQnOptions] = useState([]);
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const { showError } = useNotification();

  // friendly labels for our new keys
  const fieldLabels = {
    meterNumber: "Arvesti number",
    address: "Kinnistu",
    owner: "Omanik",
    brand: "Arvesti mark",
    qn: "Qn/m³",
    diameter: "Läbimõõt",
    lastCertificationDate: "Viimane taatlemine",
    reading: "Arvesti näit",
    removalDate: "Eemaldamise kuupäev",
    type: "Töö liik",
    workType: "Töö tüüp",
    notes: "Märkused",
  };

  // load brands and diameters for name lookup
  useEffect(() => {
    fetchMeterBrands().then(setBrandOptions).catch(console.error);
    fetchDiameters().then(setDiameterOptions).catch(console.error);
    fetchQnoms().then(setQnOptions).catch(console.error);
  }, []);

  // pull draft back out
  useEffect(() => {
    const raw = localStorage.getItem("removeData");
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed.lastCertificationDate)
      parsed.lastCertificationDate = new Date(parsed.lastCertificationDate);
    if (parsed.removalDate) parsed.removalDate = new Date(parsed.removalDate);
    setData(parsed);
  }, []);

  // Block proceeding if main gauge has sub-meters
  useEffect(() => {
    const checkSubMeters = async () => {
      if (!data?.meterNumber || data?.type !== 'MAIN_GAUGE') return;
      try {
        const meters = await searchMeters(String(data.meterNumber), true);
        if (!meters?.length) return;
        const meter = meters.find((m) => String(m.system_number) === String(data.meterNumber)) || meters[0];
        if (meter?.type === 'MAIN_GAUGE' && (meter.has_sub_meters || (meter.sub_meters_count ?? 0) > 0)) {
          showError('Sellel põhiarvestil on alamarvesteid (alam/kastmine). Eemaldamine pole lubatud.');
          // Go back to edit step
          navigate(-1);
        }
      } catch (e) {
        // If we cannot validate, stay safe and bounce back
        console.warn('Failed to verify sub-meters on Remove2', e);
        showError('Ei saa jätkata: ei õnnestunud kontrollida alamarvesteid.');
        navigate(-1);
      }
    };
    checkSubMeters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.meterNumber, data.type]);

  const formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    dt.setHours(0, 0, 0, 0);
    return dt.toLocaleDateString("et-EE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const clear = () => sigCanvas.current.clear();

  const validateBeforeSign = () => {
    // Mandatory fields before signing (exclude notes)
    const required = [
      "meterNumber",
      "reading",
      "estate",
      "client",
      "brand",
      "qn",
      "removalDate",
      "type",
    ];

    for (const key of required) {
      const val = data[key];
      if (
        val === undefined ||
        val === null ||
        (typeof val === "string" && val.trim() === "")
      ) {
        showError("Kõik lahtrid (v.a märkus) peavad olema täidetud enne allkirjastamist.");
        return false;
      }
    }
    return true;
  };

  const saveSignature = () => {
    if (!validateBeforeSign()) return;
    const sig = sigCanvas.current.getTrimmedCanvas().toDataURL();
    localStorage.setItem(
      "removeData",
      JSON.stringify({ ...data, signature: sig })
    );
    navigate("/Remove3");
  };

  const sendWithoutSignature = () => {
    localStorage.setItem(
      "removeData",
      JSON.stringify({ ...data, signature: null })
    );
    navigate("/Remove3");
  };

  const handleBackEdit = () => {
    navigate(-1);
  };

  return (
    <div className="mx-auto mt-24 p-6 bg-white shadow-lg rounded-lg border border-gray-300 max-w-5xl">
      <div className="bg-primary text-white text-center py-4 rounded-t-lg">
        <h1 className="text-xl font-bold">Kokkuvõte</h1>
      </div>
      <table className="w-full table-auto mt-4">
        <tbody>
          {Object.entries(data).map(([key, val]) => {
            if (["images", "signature", "id"].includes(key)) return null;
            if (!fieldLabels[key]) return null;

            let display = val;
            if (key === "brand") {
              const b = brandOptions.find((x) => x.id === Number(val));
              display = b?.name || "";
            } else if (key === "diameter") {
              const d = diameterOptions.find((x) => x.id === Number(val));
              display = d ? formatDecimal(d.diameter) : "";
            } else if (["lastCertificationDate", "removalDate"].includes(key)) {
              display = formatDate(val);
            } else if (key === "type") {
              const t = TYPE_OPTIONS.find((x) => x.value === val);
              display = t?.label || val;
            } else if (key === "reading") {
              display = formatDecimal(val);
            } else if (key === "qn") {
              // qn is stored as ID in draft; show its q_nom value
              const q = qnOptions.find((x) => x.id === Number(val));
              const qNom = q?.q_nom;
              display = qNom != null ? formatDecimal(qNom) : "";
            }

            return (
              <tr key={key}>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  {fieldLabels[key]}
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {display}
                </td>
              </tr>
            );
          })}

          {data.images?.length > 0 && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200 text-sm">
                Pildid
              </th>
              <td className="p-2 border-t border-gray-200">
                <div className="flex space-x-4 flex-wrap">
                  {data.images.map((src, i) => (
                    <Popup
                      key={i}
                      modal
                      trigger={
                        <img
                          src={src}
                          alt={`Foto ${i + 1}`}
                          className="w-24 h-24 object-cover rounded-md cursor-pointer"
                        />
                      }
                      closeOnDocumentClick
                    >
                      {(close) => (
                        <div className="relative p-4">
                          <button
                            onClick={close}
                            className="absolute top-2 right-2 text-2xl text-secondary bg-white rounded-full p-1"
                          >
                            <FaTimesCircle size={32} />
                          </button>
                          <img
                            src={src}
                            alt={`Foto ${i + 1}`}
                            className="w-full h-auto max-w-3xl mx-auto"
                          />
                        </div>
                      )}
                    </Popup>
                  ))}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex justify-between mt-6">
        <button
          onClick={handleBackEdit}
          className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-hover"
        >
          Tagasi
        </button>
        <div className="flex gap-4">
          <button
            onClick={sendWithoutSignature}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Saada allkirjata
          </button>
          <Popup
            modal
            trigger={
              <button className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover">
                Allkirjasta
              </button>
            }
            closeOnDocumentClick
            overlayStyle={{ backgroundColor: "rgba(0,0,0,0.6)" }}
            contentStyle={{ maxWidth: "90%", width: "1200px", margin: "auto" }}
          >
            {(close) => (
              <div className="p-4">
                <div className="bg-white p-6 rounded-md shadow-lg relative">
                  <SignatureCanvas
                    ref={sigCanvas}
                    minWidth={1}
                    maxWidth={5}
                    canvasProps={{
                      className: "border border-gray-300 rounded-md",
                      style: { width: "100%", height: "60vh" },
                    }}
                  />
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={clear}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    >
                      Tühjenda
                    </button>
                    <button
                      onClick={saveSignature}
                      className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover"
                    >
                      Salvesta
                    </button>
                  </div>
                  <button
                    onClick={close}
                    className="absolute top-2 right-2 text-2xl text-secondary bg-white rounded-full p-1"
                  >
                    <FaTimesCircle size={32} />
                  </button>
                </div>
              </div>
            )}
          </Popup>
        </div>
      </div>
    </div>
  );
}
